import { useEffect } from "react";

/*
  Scroll-reveal system (v2 — StrictMode-safe).
  Any element with a `data-reveal` attribute gently fades/slides in when it
  scrolls into view.

  Safety guarantees:
  - Tracking marks are cleaned up on unmount, so React StrictMode's
    double-mount in development can never leave elements untracked/invisible.
  - A fail-safe reveals any element that has been on screen for 1.5s without
    animating (belt-and-suspenders: content can never stay hidden).
  - Hiding CSS is scoped to body.reveal-ready, so crawlers / no-JS visitors
    always see full content.
*/
export default function useScrollReveal() {
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;

    document.body.classList.add("reveal-ready");

    const reveal = (el) => el.classList.add("revealed");

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            reveal(entry.target);
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: "0px 0px -24px 0px" }
    );

    const scan = () => {
      document
        .querySelectorAll("[data-reveal]:not(.reveal-tracked)")
        .forEach((el) => {
          el.classList.add("reveal-tracked");
          // Already in view right now? Reveal immediately — never rely
          // solely on the observer for visible content.
          const r = el.getBoundingClientRect();
          const inView =
            r.top < window.innerHeight && r.bottom > 0 && (r.width || r.height);
          if (inView) {
            // Double rAF lets the hidden state paint first,
            // so the element animates in instead of just appearing.
            requestAnimationFrame(() => requestAnimationFrame(() => reveal(el)));
          } else {
            io.observe(el);
          }
        });
    };

    scan();

    // Watch for elements added later (e.g. products loaded from Firebase)
    const mo = new MutationObserver(scan);
    mo.observe(document.body, { childList: true, subtree: true });

    // FAIL-SAFE: every 1.5s, force-reveal anything that is on screen but
    // somehow still hidden. Guarantees content can never disappear.
    const failSafe = setInterval(() => {
      document
        .querySelectorAll("[data-reveal]:not(.revealed)")
        .forEach((el) => {
          const r = el.getBoundingClientRect();
          if (r.top < window.innerHeight && r.bottom > 0 && (r.width || r.height)) {
            reveal(el);
          }
        });
    }, 1500);

    return () => {
      io.disconnect();
      mo.disconnect();
      clearInterval(failSafe);
      // CRITICAL: remove tracking marks so a re-mount (React StrictMode)
      // re-observes everything instead of skipping it.
      document
        .querySelectorAll(".reveal-tracked")
        .forEach((el) => el.classList.remove("reveal-tracked"));
      document.body.classList.remove("reveal-ready");
    };
  }, []);
}
